import { useState, useEffect } from "react";

const useFormValidation = (initialValues, validateFunction, onSubmit) => {
  const [values, setValues] = useState(initialValues);

  const [errors, setErrors] = useState({});

  const [touched, setTouched] = useState({});

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    const initialTouched = {};
    Object.keys(initialValues).forEach((key) => {
      initialTouched[key] = false;
    });
    setTouched(initialTouched);
  }, []);

  useEffect(() => {
    const validateValues = () => {
      const validationErrors = {};
      Object.keys(touched).forEach((field) => {
        if (touched[field]) {
          const error = validateFunction(field, values[field], values);
          if (error) {
            validationErrors[field] = error;
          }
        }
      });

      setErrors(validationErrors);

      const formIsValid = Object.keys(values).every((field) => {
        return (
          values[field] !== "" &&
          !validateFunction(field, values[field], values)
        );
      });

      setIsValid(formIsValid);
    };

    validateValues();
  }, [values, touched, validateFunction]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;

    if (type === "number" || e.target.getAttribute("data-type") === "numeric") {
      if (!/^\d*$/.test(value)) {
        return;
      }
    }

    setValues({
      ...values,
      [name]: value,
    });

    if (!touched[name]) {
      setTouched({
        ...touched,
        [name]: true,
      });
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched({
      ...touched,
      [name]: true,
    });
  };

  const validateAll = () => {
    const allTouched = {};
    Object.keys(initialValues).forEach((field) => {
      allTouched[field] = true;
    });
    setTouched(allTouched);

    const validationErrors = {};
    Object.keys(initialValues).forEach((field) => {
      const error = validateFunction(field, values[field], values);
      if (error) {
        validationErrors[field] = error;
      }
    });

    setErrors(validationErrors);

    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    const isFormValid = validateAll();

    if (isFormValid) {
      setIsSubmitting(true);
      try {
        await onSubmit(values);
      } catch (error) {
        console.error("Error en el envío del formulario:", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const resetForm = () => {
    setValues(initialValues);

    const resetTouched = {};
    Object.keys(initialValues).forEach((key) => {
      resetTouched[key] = false;
    });
    setTouched(resetTouched);

    setErrors({});
    setIsSubmitting(false);
  };

  const setFieldValue = (field, value) => {
    setValues({
      ...values,
      [field]: value,
    });
  };

  return {
    values,
    errors,
    touched,
    isSubmitting,
    isValid,
    handleChange,
    handleBlur,
    handleSubmit,
    resetForm,
    setFieldValue,
    validateAll,
  };
};

export default useFormValidation;
